import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, patch, post } from "../../lib/request"
import {
  buildClientMetricGoalWriteBody,
  isClientMetricGoalUnchanged,
  normalizeClientMetricGoals,
} from "../../lib/utils/clientMetricGoals"
import { skipIfClientLoaded } from "../utils/fetchDedupe"

/**
 * GET /api/v1/client-metric-goals/clients/{clientId}
 */
export const fetchClientMetricGoals = createAsyncThunk(
  "clientMetricGoals/fetch",
  async ({ clientId, force } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      const data = await get(`client-metric-goals/clients/${clientId}`)
      return {
        rows: normalizeClientMetricGoals(data),
        clientId: String(clientId),
        force: Boolean(force),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch client metric goals")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const clientId = arg?.clientId
      return !skipIfClientLoaded(getState().clientMetricGoals, clientId)
    },
  },
)

/**
 * POST /api/v1/client-metric-goals/clients/{clientId} — add / enable metric goal.
 */
export const createClientMetricGoal = createAsyncThunk(
  "clientMetricGoals/create",
  async ({ clientId, body } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      if (!body) return rejectWithValue("body is required")
      const data = await post(`client-metric-goals/clients/${clientId}`, body)
      const rows = normalizeClientMetricGoals(data)
      return {
        row: rows[0] ?? null,
        clientId: String(clientId),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create client metric goal")
    }
  },
)

/**
 * PATCH /api/v1/client-metric-goals/{id} — update existing client metric goal.
 */
export const updateClientMetricGoal = createAsyncThunk(
  "clientMetricGoals/update",
  async ({ id, body } = {}, { rejectWithValue }) => {
    try {
      const rowId = Number(id)
      if (!Number.isFinite(rowId)) return rejectWithValue("id is required")
      if (!body) return rejectWithValue("body is required")
      const data = await patch(`client-metric-goals/${rowId}`, body)
      const rows = normalizeClientMetricGoals(data)
      return { row: rows[0] ?? null, id: rowId }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update client metric goal")
    }
  },
)

/**
 * Sync selected metrics to API: POST new, PATCH updates (enabled true),
 * PATCH enabled false for unchecked existing rows.
 */
export const saveClientMetricGoals = createAsyncThunk(
  "clientMetricGoals/save",
  async (
    {
      clientId,
      selectedMetricIds,
      goalMetricTargets,
      goalMetricPeriods,
      goalMetricReach,
      goalMetricPeriodStarts,
      goalMetricPeriodValues,
      goalMetricLookup,
      existingRows,
    } = {},
    { dispatch, rejectWithValue },
  ) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      const selected = new Set(selectedMetricIds || [])
      const existingByMetricId = new Map()
      const existingByApiId = new Map()
      ;(existingRows || []).forEach((row) => {
        if (row?.metricId) existingByMetricId.set(row.metricId, row)
        const apiId = Number(row?.metricGoalId ?? row?.metricGoal?.apiId)
        if (Number.isFinite(apiId)) existingByApiId.set(apiId, row)
      })

      const findExistingRow = (metricId, metric) => {
        if (metricId && existingByMetricId.has(metricId)) return existingByMetricId.get(metricId)
        const apiId = Number(metric?.apiId)
        if (Number.isFinite(apiId) && existingByApiId.has(apiId)) return existingByApiId.get(apiId)
        return null
      }

      const upsertMetric = async (metricId, enabled) => {
        const metric = goalMetricLookup?.[metricId]
        const existing = findExistingRow(metricId, metric)
        const goal = goalMetricTargets?.[metricId] ?? existing?.goal ?? metric?.defaultTarget
        const reach = goalMetricReach?.[metricId] ?? existing?.reach ?? metric?.source ?? "Organic"
        const period = goalMetricPeriods?.[metricId] ?? existing?.periodKey ?? metric?.defaultPeriod ?? "monthly"
        const periodValue = goalMetricPeriodValues?.[metricId]
        if (
          enabled &&
          isClientMetricGoalUnchanged(existing, {
            goal,
            reach,
            period,
            enabled,
            periodValue,
          })
        ) {
          return false
        }
        const body = buildClientMetricGoalWriteBody({
          metricGoalId: metric?.apiId ?? existing?.metricGoalId,
          goal,
          reach,
          period,
          periodStartISO: goalMetricPeriodStarts?.[metricId],
          periodValue,
          enabled,
        })
        if (!body) return false

        if (existing?.id) {
          await dispatch(updateClientMetricGoal({ id: existing.id, body })).unwrap()
          return true
        }
        if (enabled) {
          await dispatch(createClientMetricGoal({ clientId, body })).unwrap()
          return true
        }
        return false
      }

      let wrote = false
      for (const metricId of selected) {
        if (await upsertMetric(metricId, true)) wrote = true
      }

      for (const row of existingRows || []) {
        if (!row.id) continue
        const metric = Object.values(goalMetricLookup || {}).find(
          (m) => Number(m?.apiId) === Number(row.metricGoalId),
        )
        const stillSelected =
          (row.metricId && selected.has(row.metricId)) ||
          (metric?.id && selected.has(metric.id))
        if (stillSelected) continue
        if (row.enabled === false) continue
        const body = buildClientMetricGoalWriteBody({
          metricGoalId: row.metricGoalId ?? metric?.apiId,
          goal: goalMetricTargets?.[row.metricId] ?? row.goal,
          reach: goalMetricReach?.[row.metricId] ?? row.reach,
          period: goalMetricPeriods?.[row.metricId] ?? row.periodKey,
          enabled: false,
        })
        if (!body) continue
        await dispatch(updateClientMetricGoal({ id: row.id, body })).unwrap()
        wrote = true
      }

      if (!wrote) {
        return { clientId: String(clientId), rows: existingRows || [], wrote: false }
      }

      const refreshed = await dispatch(
        fetchClientMetricGoals({ clientId, force: true }),
      ).unwrap()
      return { clientId: String(clientId), rows: refreshed.rows ?? [], wrote: true }
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err?.message || err?.payload || "Failed to save client metric goals"
      return rejectWithValue(message)
    }
  },
)

const initialState = {
  rows: [],
  status: "idle",
  error: null,
  requestedClientId: null,
  fetchedClientId: null,
  saveStatus: "idle",
  saveError: null,
}

function beginFetch(state, clientId) {
  state.status = "loading"
  state.error = null
  state.requestedClientId = clientId != null ? String(clientId) : null
  state.fetchedClientId = null
  state.rows = []
}

function isStaleFetch(action, state) {
  const clientId = action.meta.arg?.clientId
  const key = clientId != null ? String(clientId) : null
  return key !== state.requestedClientId
}

function upsertRowInState(state, row) {
  if (!row?.id) return
  const idx = state.rows.findIndex((r) => r.id === row.id)
  if (idx >= 0) state.rows[idx] = row
  else state.rows.push(row)
}

const clientMetricGoalsSlice = createSlice({
  name: "clientMetricGoals",
  initialState,
  reducers: {
    clearClientMetricGoals: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientMetricGoals.pending, (state, action) => {
        beginFetch(state, action.meta.arg?.clientId)
      })
      .addCase(fetchClientMetricGoals.fulfilled, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "succeeded"
        state.rows = action.payload?.rows ?? []
        state.fetchedClientId = action.payload?.clientId ?? null
      })
      .addCase(fetchClientMetricGoals.rejected, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch client metric goals"
        state.rows = []
        state.fetchedClientId = state.requestedClientId
      })
      .addCase(createClientMetricGoal.fulfilled, (state, action) => {
        upsertRowInState(state, action.payload?.row)
      })
      .addCase(updateClientMetricGoal.fulfilled, (state, action) => {
        upsertRowInState(state, action.payload?.row)
      })
      .addCase(saveClientMetricGoals.pending, (state) => {
        state.saveStatus = "loading"
        state.saveError = null
      })
      .addCase(saveClientMetricGoals.fulfilled, (state, action) => {
        state.saveStatus = "succeeded"
        state.rows = action.payload?.rows ?? state.rows
        state.fetchedClientId = action.payload?.clientId ?? state.fetchedClientId
        state.status = "succeeded"
      })
      .addCase(saveClientMetricGoals.rejected, (state, action) => {
        state.saveStatus = "failed"
        state.saveError = action.payload ?? "Failed to save client metric goals"
      })
  },
})

export const { clearClientMetricGoals } = clientMetricGoalsSlice.actions
export default clientMetricGoalsSlice.reducer
